import React, { useState, useEffect } from 'react';
import { Link, Plus, Edit3, Trash2, Save, X, ChevronDown, ArrowLeft } from 'lucide-react';
import timetableService from '../../services/timetableService';
import '../../styles/AssociatedSubjectSettings.css';

const AssociatedSubjectSettings = () => {
  const [associatedSubjects, setAssociatedSubjects] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [mode, setMode] = useState('Add'); // Add, Modify, Delete, Cancel, Save
  const [formData, setFormData] = useState({
    associated_key: '',
    subject_item_key: '',
    subject_item_name: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [associations, subjects] = await Promise.all([
        timetableService.getAssociatedSubjects(),
        timetableService.getSubjectGroups()
      ]);
      setAssociatedSubjects(associations);
      setSubjectGroups(subjects);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssociationSelect = (association) => {
    setSelectedAssociation(association);
    setFormData({
      associated_key: association.associated_key || '',
      subject_item_key: association.subject_item_key || '',
      subject_item_name: association.subject_item_name || ''
    });
    setMode('Modify');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubjectItemChange = (subjectItemKey) => {
    const selectedSubject = subjectGroups.find(s => s.subject_group_key === subjectItemKey);
    setFormData(prev => ({
      ...prev,
      subject_item_key: subjectItemKey,
      subject_item_name: selectedSubject?.subject_group_name || ''
    }));
  };

  const handleAdd = () => {
    setMode('Add');
    setFormData({
      associated_key: '',
      subject_item_key: '',
      subject_item_name: ''
    });
  };

  const handleModify = () => {
    setMode('Modify');
  };

  const handleDelete = async () => {
    if (selectedAssociation && window.confirm('確定要刪除此相連科目嗎？')) {
      try {
        await timetableService.deleteAssociatedSubject(selectedAssociation.id);
        await loadData();
        setMode('Add');
      } catch (error) {
        console.error('Error deleting associated subject:', error);
      }
    }
  };

  const handleCancel = () => {
    if (selectedAssociation) {
      setFormData({
        associated_key: selectedAssociation.associated_key || '',
        subject_item_key: selectedAssociation.subject_item_key || '',
        subject_item_name: selectedAssociation.subject_item_name || ''
      });
    } else {
      setFormData({
        associated_key: '',
        subject_item_key: '',
        subject_item_name: ''
      });
    }
    setMode('Add');
  };

  const handleSave = async () => {
    try {
      if (mode === 'Add') {
        await timetableService.createAssociatedSubject(formData);
      } else if (mode === 'Modify' && selectedAssociation) {
        await timetableService.updateAssociatedSubject(selectedAssociation.id, formData);
      }
      await loadData();
      setMode('Add');
    } catch (error) {
      console.error('Error saving associated subject:', error);
    }
  };

  if (loading) {
    return (
      <div className="associated-subject-settings-container">
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
            </div>
            <p className="loading-text">載入相連科目資料中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="associated-subject-settings-container">
      {/* Header */}
      <div className="associated-subject-settings-header">
        <h2>Associated Subject Items (TBM0155E)</h2>
        <p className="subtitle">相連科目設定 - 管理相連科目項目</p>
      </div>

      {/* Main Content */}
      <div className="associated-subject-settings-content">
        {/* Association Form Panel */}
        <div className="association-form-panel">
          <h3 className="association-form-title">Associated Subject Items (TBM0155E)</h3>
          
          <div className="association-form-grid">
            {/* Key and Subject Item Association */}
            <div className="association-form-group association-row">
              <div className="association-form-group">
                <label className="association-form-label">Key:</label>
                <input
                  type="text"
                  value={formData.associated_key}
                  onChange={(e) => handleInputChange('associated_key', e.target.value)}
                  className="association-form-input"
                  placeholder="RCH"
                />
              </div>
              
              <div className="association-text">
                to associate with
              </div>
              
              <div className="association-form-group">
                <label className="association-form-label">Subject Item:</label>
                <div className="association-input-with-button">
                  <select
                    value={formData.subject_item_key}
                    onChange={(e) => handleSubjectItemChange(e.target.value)}
                    className="association-form-input"
                  >
                    <option value="">選擇科目項目</option>
                    {subjectGroups.map((subject) => (
                      <option key={subject.id} value={subject.subject_group_key}>
                        {subject.subject_group_key} - {subject.subject_group_name}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="association-input-button">
                    <ChevronDown size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Subject Item Name Display */}
            {formData.subject_item_name && (
              <div className="selected-subject-display">
                <label className="selected-subject-label">
                  選定的科目項目名稱:
                </label>
                <div className="selected-subject-name">
                  {formData.subject_item_name}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons Panel */}
        <div className="action-buttons-panel">
          <button
            onClick={() => {/* Navigate back to Subject Items */}}
            className="action-button secondary"
          >
            <ArrowLeft size={16} />
            Prev Page
          </button>
          <button
            onClick={handleAdd}
            className="action-button success"
          >
            <Plus size={16} />
            Add
          </button>
          <button
            onClick={handleModify}
            className="action-button primary"
          >
            <Edit3 size={16} />
            Modify
          </button>
          <button
            onClick={handleDelete}
            className="action-button danger"
          >
            <Trash2 size={16} />
            Delete
          </button>
          <button
            onClick={handleCancel}
            className="action-button secondary"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button success"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Association List Panel */}
      <div className="association-list-panel">
        <div className="association-list-header">
          <h3 className="association-list-title">Current Associated Subject Items:</h3>
          <span className="association-list-count">{associatedSubjects.length}</span>
        </div>
        
        <div className="association-list">
          {associatedSubjects.map((association, index) => (
            <div
              key={association.id}
              onClick={() => handleAssociationSelect(association)}
              className={`association-item ${selectedAssociation?.id === association.id ? 'selected' : ''}`}
            >
              <div className="association-item-code">
                {association.associated_key}-{association.subject_item_key}
              </div>
              <div className="association-item-details">
                Associated Key: {association.associated_key} | Subject Item: {association.subject_item_key} | Name: {association.subject_item_name}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mode Indicator */}
      <div className="mode-indicator">
        Mode: {mode}
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">相連科目設定指引</h4>
        <div className="instructions-content">
          <p><strong>1. 按「增新」(Add)鍵增加新的相連科目：</strong> 點擊「增新」按鈕開始新增相連科目。</p>
          <p><strong>2. 在「索引」(Key)欄位鍵入新相連科目的索引名稱：</strong> 輸入相連科目的唯一索引名稱。</p>
          <p><strong>3. 拉下「科目項目」(Subject Item)選單，選擇欲連繫的原有科目：</strong> 從下拉選單中選擇要關聯的科目項目。</p>
          <p><strong>4. 輸入完成後，按「儲存」(Save)鍵紀錄資料或按「取消」(Cancel)鍵取消儲存資料：</strong> 完成輸入後選擇儲存或取消。</p>
          <p><strong>5. 用者可按「編修」(Modify)鍵編修已輸入的相連科目：</strong> 重覆步驟2至4即可完成編修程序。</p>
          <p><strong>6. 用者亦可按「刪除」(Delete)鍵删除已輸入的相連科目：</strong> 刪除不需要的相連科目關聯。</p>
        </div>
      </div>
    </div>
  );
};

export default AssociatedSubjectSettings;
